import { useContext, useEffect, useState } from "react";

import { useRouter } from "next/router";

import * as Yup from "yup";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import AuthContext from "shared/providers/context/AuthContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import useWindowSize from "shared/utils/hooks/useWindowSize";
import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import Button from "@CheckoutOnepageOffers/components/atoms/Button";
import BillingInformation from "@CheckoutOnepageOffers/components/molecules/BillingInformation";
import Coupon from "@CheckoutOnepageOffers/components/molecules/Coupon";
import CreditCardForm from "@CheckoutOnepageOffers/components/molecules/CreditCardForm";
import DiscountInformation from "@CheckoutOnepageOffers/components/molecules/DiscountInformation";
import DiscountPrice from "@CheckoutOnepageOffers/components/molecules/DiscountPrice";
import FinalPricesOrderBump from "@CheckoutOnepageOffers/components/molecules/FinalPricesOrderBump";
import PaymentMethods from "@CheckoutOnepageOffers/components/molecules/PaymentMethods";
import PersonalData from "@CheckoutOnepageOffers/components/molecules/PersonalData";
import PriceInformation, {
  WrapperPriceInformation,
} from "@CheckoutOnepageOffers/components/molecules/PriceInformation";
import ProcessTax from "@CheckoutOnepageOffers/components/molecules/ProcessTax";
import ProductInfoMobile from "@CheckoutOnepageOffers/components/molecules/ProductInfoMobile";
import SupportInformation from "@CheckoutOnepageOffers/components/molecules/SupportInformation";
import { useCheckoutOnepageOffersContext } from "@CheckoutOnepageOffers/provider/useCheckoutOnepageOffersContext";
import { getSchemaValidation } from "@CheckoutOnepageOffers/schema/getSchemaValidation";
import { Title } from "@CheckoutOnepageOffers/styles/Title";
import { paymentDataModeling } from "@CheckoutOnepageOffers/utils/paymentDataModeling";
import { IInstallment } from "@CheckoutStepsOffers/@types/offer";
import { IOptionsProps } from "@CheckoutStepsOffers/components/atoms/Select";
import {
  getInstallmentOptions,
  getInstallmentsOrderBump,
} from "@CheckoutStepsOffers/utils/getInstallments";
import { handleSelectOrderBump } from "@Console/order_bump/utils/handleSelectOrderBump";
import DescriptionBottom from "@payment/components/DescriptionBottom";
import SelectCountry from "@payment/components/SelectCountry";
import OrderBumpCTA, {
  WrapperOrderBumpCTA,
} from "@payment/components/molecules/OrderBumpCTA";
import SpecialOffersOrderBump from "@payment/components/molecules/SpecialOffersOrderBump";
import useCheckoutNavigationOffers from "@payment/hooks/useCheckoutNavigationOffers";
import usePayOffers from "@payment/hooks/usePayOffers";
import { useCheckoutContext } from "@payment/providers/CheckoutProvider/useCheckoutContext";
import { checkCreditCardId } from "@payment/utils/checkCreditCardId";
import { generateEncryptedReference } from "@payment/utils/generateEncryptedReference";
import { getPaymentMethod } from "@payment/utils/getPaymentMethod";
import logToCheckoutMonitoring from "@payment/utils/logToCheckoutMonitoring";
import { savePaymentDetails } from "@payment/utils/savePaymentDetails";
import { scrollToError } from "@payment/utils/scrollToError";
import { captureException, captureMessage } from "@sentry/nextjs";
import { Scope, SubmitHandler } from "@unform/core";

import { Container, Form, LoadingIcon, PurchaseSummary } from "./styles";
import { IFormData } from "./type";

const PurchaseForm = () => {
  const router = useRouter();
  const { width } = useWindowSize();

  const { state } = useGlobalContext();
  const {
    addressState,
    userSettingsState,
    setNewUserSettings,
    country,
    setCountry,
    setCountryDDI,
    setCountryId,
    countryId,
    clientIp,
  } = useCheckoutContext();
  const { loggedUser, signInCheckoutOnePage } = useContext(AuthContext);

  const { addressList } = addressState;
  const { userSettings } = userSettingsState;
  const addressId = userSettings?.payment?.address_id;

  let dataForCheckoutMonitoring: any = "Sem dados";

  const condition = addressList?.filter((item: any) => item.id === addressId);
  const addressDefault: any =
    (condition && condition[0]) || (addressList?.length >= 1 && addressList[0]);

  const {
    offer,
    formRef,
    promoterLink,
    userCanTrial,
    setUserCanTrial,
    checkoutOnePageState,
    checkoutOnePageDispatch,
    initialData: userInitialData,

    orderBumpsSelected,
    orderBumpsInstallmentSelected,

    setOrderBumpsSelected,
    setOrderBumpsInstallmentSelected,

    isPhoneNumberValid,
  } = useCheckoutOnepageOffersContext();

  const [inputError, setInputError] = useState<string>("");

  const phoneNumber = !!loggedUser?.phones?.length
    ? loggedUser?.phones[0]
    : null;

  const initialData = {
    user: {
      name:
        userSettings?.payment?.display_name ||
        userSettings?.payment?.fullname ||
        loggedUser?.display_name ||
        loggedUser?.full_name ||
        userInitialData?.name,

      email: (
        userSettings?.payment?.email || userInitialData?.email
      )?.toLowerCase(),
      phone_number:
        phoneNumber?.full_number?.slice(1) ||
        userSettings?.payment?.phone?.slice(3) ||
        userInitialData?.phone,
      document:
        userSettings?.payment?.document ||
        loggedUser?.document ||
        userInitialData?.document,
    },
    address: addressDefault,
  };

  const containers = {
    boleto: <BillingInformation />,
    credit_card: <CreditCardForm />,
    pix: <BillingInformation />,
    [""]: <></>,
  };

  const { create_account_options } = state.tenancy;

  let errorDisplayed = false;

  const handleSubmit: SubmitHandler<IFormData> = async (data) => {
    checkoutOnePageDispatch({
      type: "SET_CHECKOUT_ONE_PAGE_SETTINGS",
      payload: {
        loader: true,
      },
    });

    try {
      const filterData = Object.keys(data).filter(
        (keys) => keys !== "installments",
      );
      const hasValidationToMake = filterData.length;

      if (hasValidationToMake) {
        const schema = getSchemaValidation({
          create_account_options,
          loggedUser: !!loggedUser,
          address: !!data?.address,
          methodPaymentSelected: checkoutOnePageState.methodPaymentSelected,
          country,
        });

        await schema.validate(data, {
          abortEarly: false,
        });
      }

      const payloadDataModeling = {
        data,
        loggedUser,
        promoterLink,
        userSettings,
        offer: offer,
        payment_method: checkCreditCardId(
          checkoutOnePageState.methodPaymentSelected,
        )
          ? "credit_card"
          : checkoutOnePageState.methodPaymentSelected,
      };

      if (checkCreditCardId(checkoutOnePageState.methodPaymentSelected)) {
        payloadDataModeling["cardId"] = Number(
          checkoutOnePageState.methodPaymentSelected,
        );

        if (!offer.offer.details.default_address)
          payloadDataModeling["addressId"] = addressDefault?.id;
      }

      const modeledData = {
        ...paymentDataModeling(payloadDataModeling),
        country_id: countryId,
        clientIp,
      };

      if (checkoutOnePageState?.coupon)
        modeledData["coupon"] = checkoutOnePageState?.coupon.item.coupon_code;

      if (!userCanTrial) modeledData["ignore_trial"] = true;

      if (!!orderBumpsSelected?.length)
        modeledData["order_bump"] = orderBumpsSelected.map((orderBump) => ({
          id: orderBump?.id,
          installments:
            orderBumpsInstallmentSelected[orderBump?.slug]?.installment
              ?.installment,
        }));

      const emailInLowerCase = (
        data?.user?.email || loggedUser?.email
      )?.toLowerCase();

      modeledData["reference"] = generateEncryptedReference(
        emailInLowerCase,
        getPaymentMethod(checkoutOnePageState.methodPaymentSelected),
      );
      modeledData["email"] = emailInLowerCase;
      modeledData["email_confirmation"] = emailInLowerCase;
      dataForCheckoutMonitoring = modeledData;

      const response = await usePayOffers(modeledData);

      if (response.status !== 200) errorDisplayed = true;

      const transaction = !!response.data?.transactions?.length
        ? response.data?.transactions[0]
        : null;

      if (!loggedUser && !!response.data.token) {
        signInCheckoutOnePage({
          token: response.data?.token,
          user: response.data?.user,
        });
      }

      if (loggedUser || response.data.token)
        savePaymentDetails(modeledData, setNewUserSettings);

      const IS_CREDIT_CARD =
        checkoutOnePageState.methodPaymentSelected === "credit_card" ||
        checkCreditCardId(checkoutOnePageState.methodPaymentSelected);
      const IS_REDIRECT_TYPE = offer.offer.details.redirect_type === "custom";
      const NO_HAS_VALUE_TO_PAY =
        checkoutOnePageState?.coupon?.item?.price_with_discount === 0;

      if (
        (IS_CREDIT_CARD && IS_REDIRECT_TYPE) ||
        (IS_REDIRECT_TYPE && NO_HAS_VALUE_TO_PAY)
      )
        return window.open(offer.offer.details.redirect_url, "_self");

      const paymentInfo = {
        email: emailInLowerCase,
        reference: generateEncryptedReference(
          emailInLowerCase,
          getPaymentMethod(checkoutOnePageState.methodPaymentSelected),
        ),
        promoterLink: promoterLink,
        paymentStatus:
          transaction?.status === "waiting_payment"
            ? "awaitingPayment"
            : "approvedPayment",
        requestBody: modeledData,
        ...response.data,
      };

      useCheckoutNavigationOffers(
        router,
        getPaymentMethod(checkoutOnePageState.methodPaymentSelected),
        paymentInfo,
      );
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};

        error.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });

        formRef.current.setErrors(validationErrors);

        const firstError = error.inner[0].path;
        scrollToError(firstError);

        console.table(error.inner);

        errorDisplayed = true;

        openNotificationWithIcon(
          "error",
          "Campos incorretos",
          "Preencha os campos corretamente antes de prosseguir",
        );
      }

      if (
        error?.response?.status !== 200 &&
        error?.response?.status !== 500 &&
        error?.response !== undefined
      ) {
        const { errors, message, type } = error?.response?.data;

        const err = errors ? Object.values(errors) : message;

        logToCheckoutMonitoring(err, {
          ...dataForCheckoutMonitoring,
          clientIp,
        });

        const errorsMessage = Array.isArray(err)
          ? err.map((error) => <li key={error}>{error[0]}</li>)
          : err?.message || err?.attrs?.message || err;

        errorDisplayed = true;

        openNotificationWithIcon(
          "error",
          "Erro ao processar pagamento",
          <ul>{errorsMessage}</ul>,
          6,
        );

        if (
          type === "warning" &&
          error?.response?.status === 400 &&
          message ===
            "Usuário com este e-mail não pode usar o periodo de avaliação para esta oferta."
        )
          errorDisplayed = true;

        setUserCanTrial(false);

        console.log(error);
      }

      if (!errorDisplayed)
        openNotificationWithIcon(
          "error",
          "Não foi possível realizar esta compra no momento. Por favor, tente novamente mais tarde.",
        );

      const errorMonitoringMessage = `PaymentWithoutProvider (handleSubmit): ${error}`;

      console.log(errorMonitoringMessage);
      captureException(error);
      captureMessage(errorMonitoringMessage);
    } finally {
      checkoutOnePageDispatch({
        type: "SET_CHECKOUT_ONE_PAGE_SETTINGS",
        payload: {
          loader: false,
        },
      });
    }
  };

  const handleChangeCountry = (selectedOption) => {
    setCountry(selectedOption?.name);
    setCountryDDI(selectedOption?.ddi);
    setCountryId(selectedOption?.code);
    setNewCreditCardPayment();
  };

  useEffect(() => {
    if (country !== "Brasil") {
      if (orderBumpsSelected?.length > 0) {
        orderBumpsSelected.forEach((orderBump) => {
          const installments = getInstallmentsOrderBump(orderBump);
          const installmentsOptions = getInstallmentOptions(installments);

          const firstInstallment = installments[0];
          const firstOption = installmentsOptions[0];

          if (firstOption) {
            setOrderBumpsInstallmentSelected((prevState) => ({
              ...prevState,
              [orderBump?.slug]: {
                option: {
                  value: firstOption?.value,
                  text: firstOption?.text,
                },
                installment: firstInstallment as IInstallment,
              },
            }));
          }
        });
      }
    }
  }, [country]);

  const setNewCreditCardPayment = () => {
    checkoutOnePageDispatch({
      type: "SET_CHECKOUT_ONE_PAGE_SETTINGS",
      payload: {
        methodPaymentSelected: "credit_card",
      },
    });
  };

  return (
    <Container className="purchase-form-container">
      <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
        <div className="report-with-select-country">
          {width <= 690 ? (
            <>
              <div className="mobile-select-country">
                <SelectCountry handleChange={handleChangeCountry} />
              </div>
              <ProductInfoMobile />
              {orderBumpsSelected?.length > 0 && (
                <div className="special-offer-order-bump">
                  <SpecialOffersOrderBump
                    orderBumpsSelected={orderBumpsSelected}
                    methodPaymentSelected={
                      checkoutOnePageState.methodPaymentSelected
                    }
                    orderBumpsInstallmentSelected={
                      orderBumpsInstallmentSelected
                    }
                  />
                </div>
              )}
            </>
          ) : (
            <Title>Informações de pagamento</Title>
          )}
          {width > 690 && <SelectCountry handleChange={handleChangeCountry} />}
        </div>

        <PersonalData inputError={inputError} setInputError={setInputError} />
        {width <= 690 && <Coupon />}
        <PaymentMethods />
        {containers[checkoutOnePageState.methodPaymentSelected]}

        {offer?.order_bump?.map((order_bump, index) => (
          <ComponentErrorBoundary key={order_bump?.id}>
            <WrapperOrderBumpCTA key={order_bump?.id}>
              <Scope path={`order_bump[${index}]`}>
                <OrderBumpCTA
                  orderBump={order_bump}
                  paymentMethodSelected={
                    checkoutOnePageState.methodPaymentSelected
                  }
                  onSelectInstallments={(
                    option: IOptionsProps,
                    installment: IInstallment,
                  ) =>
                    setOrderBumpsInstallmentSelected((prevState) => ({
                      ...prevState,
                      [order_bump?.slug]: {
                        option,
                        installment,
                      },
                    }))
                  }
                  handleSelectOrderBump={() =>
                    handleSelectOrderBump(order_bump, setOrderBumpsSelected)
                  }
                />
              </Scope>
            </WrapperOrderBumpCTA>
          </ComponentErrorBoundary>
        ))}

        {checkoutOnePageState?.methodPaymentSelected &&
          width <= 690 &&
          orderBumpsSelected?.length === 0 && (
            <WrapperPriceInformation>
              <DiscountPrice />

              <ProcessTax />

              <PriceInformation />

              <DiscountInformation />
            </WrapperPriceInformation>
          )}

        {orderBumpsSelected?.length > 0 && width <= 690 && (
          <>
            <PurchaseSummary>Resumo da compra</PurchaseSummary>
            <FinalPricesOrderBump />
          </>
        )}
        <Button
          type="submit"
          loading={checkoutOnePageState.loader}
          disabled={
            !checkoutOnePageState.methodPaymentSelected ||
            checkoutOnePageState.loader ||
            !!inputError ||
            !isPhoneNumberValid
          }
        >
          {checkoutOnePageState.loader && <LoadingIcon />}
          {!!offer?.offer?.details?.trial_enabled ? (
            <>Experimente grátis por {offer?.offer?.details?.trial_days} dias</>
          ) : (
            <>Finalizar compra</>
          )}
        </Button>
      </Form>
      {!loggedUser && <DescriptionBottom />}
      {width <= 690 && <SupportInformation />}
    </Container>
  );
};

export default PurchaseForm;
